import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useGameStore } from './store/gameStore'
import { useSettingsStore } from './store/settingsStore'
import { loadDebugMode, type DebugMode } from './engine/debugGameSetup'
import MainGameplay from './components/game/MainGameplay'
import { ErrorBoundary } from './components/game/ErrorBoundary'
import { RulesPage } from './components/rules/RulesPage'
import { LandingPage, ColorBarNav } from './pages/LandingPage'
import { PlayerCountSelection } from './pages/PlayerCountSelection'
import { GalleryPage } from './pages/GalleryPage'
import TutorialPage from './pages/TutorialPage'
import { SettingsPage } from './pages/SettingsPage'
import { FloatingCardsBackground } from './components/background/FloatingCardsBackground'
import { ThemeProvider } from './components/theme/ThemeProvider'
import { usePersistentAudio, startMusicIfEnabled } from './hooks/useAudioManager'

// ============================================================================
// PAGE WRAPPERS
// ============================================================================

function LandingPageWrapper() {
  const navigate = useNavigate()

  const handlePlay = () => {
    startMusicIfEnabled()
    navigate('/player-count')
  }

  return (
    <>
      <ColorBarNav onNavigate={(item) => {
        if (item === 'rules') navigate('/rules', { state: { from: '/' } })
        if (item === 'gallery') navigate('/gallery', { state: { from: '/' } })
        if (item === 'tutorial') navigate('/tutorial', { state: { from: '/' } })
        if (item === 'settings') navigate('/settings', { state: { from: '/' } })
      }} />
      <LandingPage
        onPlay={handlePlay}
        onTutorial={() => navigate('/tutorial', { state: { from: '/' } })}
      />
    </>
  )
}

function PlayerCountSelectionWrapper() {
  const navigate = useNavigate()
  const location = useLocation()
  const { startGameFromSetup, setPlayerCount: storeSetPlayerCount } = useGameStore()

  const handlePlayerCountSelect = (count: number, playerStarts: boolean = false, debugMode?: DebugMode) => {
    storeSetPlayerCount(count as 3 | 4 | 5)

    if (debugMode && debugMode !== 'none') {
      // Use isolated debug setup
      loadDebugMode(debugMode, count as 3 | 4 | 5)
      navigate('/game')
      return
    }

    // Normal game setup
    startGameFromSetup()

    const { gameState } = useGameStore.getState()
    if (gameState) {
      if (playerStarts) {
        console.log('[handlePlayerCountSelect] Human selected to go first')
        useGameStore.getState().setFirstPlayerIndex(0)
      } else {
        const randomIndex = Math.floor(Math.random() * count)
        console.log('[handlePlayerCountSelect] Randomly selected first player:', randomIndex, 'Player:', gameState.players[randomIndex]?.name)
        useGameStore.getState().setFirstPlayerIndex(randomIndex)
      }
    }

    navigate('/game')
  }

  const handleBack = () => {
    // Navigate back to where we came from, or home if no state
    const from = location.state?.from
    navigate(from || '/')
  }

  return (
    <>
      <ColorBarNav onNavigate={(item) => {
        if (item === 'rules') navigate('/rules', { state: { from: '/player-count' } })
        if (item === 'gallery') navigate('/gallery', { state: { from: '/player-count' } })
        if (item === 'tutorial') navigate('/tutorial', { state: { from: '/player-count' } })
        if (item === 'settings') navigate('/settings', { state: { from: '/player-count' } })
      }} />
      <PlayerCountSelection
        onSelect={handlePlayerCountSelect}
        onBack={handleBack}
      />
    </>
  )
}

function RulesPageWrapper() {
  const navigate = useNavigate()
  const location = useLocation()

  const handleBack = () => {
    // Navigate back to where we came from, or home if no state
    const from = location.state?.from
    navigate(from || '/')
  }

  return (
    <>
      <RulesPage onBack={handleBack} />
    </>
  )
}

function GalleryPageWrapper() {
  const navigate = useNavigate()
  const location = useLocation()

  const handleBack = () => {
    // Navigate back to where we came from, or home if no state
    const from = location.state?.from
    navigate(from || '/')
  }

  return (
    <>
      <ColorBarNav onNavigate={(item) => {
        if (item === 'rules') navigate('/rules', { state: { from: '/gallery' } })
        if (item === 'gallery') navigate('/gallery')
        if (item === 'tutorial') navigate('/tutorial', { state: { from: '/gallery' } })
        if (item === 'settings') navigate('/settings', { state: { from: '/gallery' } })
      }} />
      <GalleryPage onBack={handleBack} />
    </>
  )
}

function GamePageWrapper() {
  const navigate = useNavigate()
  const { resetGame, gameState } = useGameStore()

  const handleReturnToMenu = () => {
    // Clear game state when returning to menu
    resetGame()
    navigate('/')
  }

  const handleHomeClick = () => {
    // Just navigate - don't clear state (avoids redirect to player-count)
    // State will be cleared when starting a new game or explicitly exiting
    navigate('/')
  }

  const handleSettingsClick = () => {
    navigate('/settings', { state: { from: '/game' } })
  }

  if (!gameState) {
    return <Navigate to="/player-count" replace />
  }

  return (
    <ErrorBoundary>
      <MainGameplay onExitToMenu={handleReturnToMenu} onHomeClick={handleHomeClick} onSettingsClick={handleSettingsClick} />
    </ErrorBoundary>
  )
}

function TutorialPageWrapper() {
  return <TutorialPage />
}

function SettingsPageWrapper() {
  const navigate = useNavigate()
  const location = useLocation()

  // Determine where to go back to based on how we got here
  const handleBack = () => {
    // Check if we came from a specific page
    if (location.state?.from) {
      navigate(location.state.from)
      return
    }

    // Check if we have gameState (meaning we came from game)
    const hasGameState = useGameStore.getState().gameState !== null
    if (hasGameState) {
      navigate('/game')
    } else {
      navigate('/')
    }
  }

  return (
    <>
      <ColorBarNav onNavigate={(item) => {
        if (item === 'rules') navigate('/rules', { state: { from: location.state?.from || '/' } })
        if (item === 'gallery') navigate('/gallery', { state: { from: location.state?.from || '/' } })
        if (item === 'tutorial') navigate('/tutorial', { state: { from: location.state?.from || '/' } })
        if (item === 'settings') navigate('/settings')
      }} />
      <SettingsPage onBack={handleBack} />
    </>
  )
}

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================

function App() {
  const location = useLocation()
  const { background } = useSettingsStore()
  const isGameRoute = location.pathname === '/game' || location.pathname === '/tutorial'

  // Initialize persistent audio - keeps music playing across all pages
  usePersistentAudio()

  // Show animated background:
  // - Always on non-game routes
  // - On game route only if enabled in settings
  const showBackground = !isGameRoute || (isGameRoute && background.animatedCardsEnabled)

  return (
    <ThemeProvider>
      {/* Persistent background - always shown on landing/rules/etc, conditional during game */}
      {showBackground && <FloatingCardsBackground />}

      {/* Route content */}
      <Routes>
        <Route path="/" element={<LandingPageWrapper />} />
        <Route path="/player-count" element={<PlayerCountSelectionWrapper />} />
        <Route path="/rules" element={<RulesPageWrapper />} />
        <Route path="/gallery" element={<GalleryPageWrapper />} />
        <Route path="/tutorial" element={<TutorialPageWrapper />} />
        <Route path="/settings" element={<SettingsPageWrapper />} />
        <Route path="/game" element={<GamePageWrapper />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ThemeProvider>
  )
}

export default App
